'use es6';

import { connect } from 'react-redux';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { STACK, VERTICAL_ALIGN } from 'EmailEditorUI/constants/SectionLayoutConstants';
import RowDOM from 'LayoutBuilderUI/components/gridImplementations/RowDOM';
import Column from 'LayoutBuilderUI/components/Column';
import { getResizeGraduationForSection, getMinColumnWidthForGraduationSizeHelper, getCanAddNewColumnHelper, getShouldShowResizeHandles } from 'LayoutBuilderUI/selectors/resizeSelectors';
import { resizeColumns } from 'LayoutBuilderUI/actions/userInterfaceActions';
import { addNewColumn, removeColumn } from 'LayoutBuilderUI/actions/layoutActions';
import { logInteraction } from 'LayoutBuilderUI/actions/ApiActions';
import { AsyncResizeArea, AsyncColumnResizeHandle } from 'LayoutBuilderUI/components/AsyncComponents';
import { getIsInMobileBreakpointMode } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { getDefaultStyle } from 'EmailEditorUI/utils/breakpointUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapStateToProps = (state, props) => {
  const showResize = getShouldShowResizeHandles(state) && (props.hovered || props.isSelected);
  let maybeProps = {};
  const canResize = !props.isSingleColumnFullWidth;
  if (canResize) {
    const resizeGraduationSize = getResizeGraduationForSection(state, props.index);
    const minColumnWidth = getMinColumnWidthForGraduationSizeHelper(resizeGraduationSize);
    maybeProps = {
      resizeGraduationSize,
      minColumnWidth,
      canAddNewColumn: getCanAddNewColumnHelper(props.section.get('columns').size, minColumnWidth)
    };
  }
  return Object.assign({
    showResize,
    canResize,
    isMobileBreakpointMode: getIsInMobileBreakpointMode(state)
  }, maybeProps);
};
class RowDOMContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      resizingIndex: -1,
      columnsResizeWidth: []
    };
    this.startColumnResize = this.startColumnResize.bind(this);
    this.stopColumnResize = this.stopColumnResize.bind(this);
    this.resizeColumns = this.resizeColumns.bind(this);
    this.getFinalColumnWidthsFromState = this.getFinalColumnWidthsFromState.bind(this);
  }
  componentDidUpdate(prevProps) {
    if (this.props.section !== prevProps.section) {
      this.setState({
        resizingIndex: -1,
        columnsResizeWidth: []
      });
    }
  }
  get columns() {
    if (this.state.resizingIndex === -1) return this.props.section.get('columns');
    return this.props.section.get('columns').map((c, idx) => c.set('width', this.state.columnsResizeWidth[idx - this.state.resizingIndex] || c.get('width')));
  }
  get resizeHandleOffsets() {
    if (!this.props.showResize || !this.props.canResize) {
      return [];
    }
    return this.columns.reduce((sums, col, colIdx) => {
      const columnWidth = Math.round(col.get('width') * this.props.resizeGraduationSize);
      if (sums.length) {
        return [...sums, sums[colIdx - 1] + columnWidth];
      }
      return [columnWidth];
    }, []);
  }
  getResizePoints(columnIndex, columnWidths) {
    const resizePoints = [];
    let resizePoint;
    const resizeArea = columnWidths[0] + columnWidths[1];
    if (resizeArea <= this.props.minColumnWidth * 2) {
      return [];
    }
    resizePoint = this.props.minColumnWidth;
    while (resizePoint + this.props.minColumnWidth <= resizeArea) {
      resizePoints.push(resizePoint);
      resizePoint += 1;
    }
    let startOffset = 0;
    for (let i = 0; i < columnIndex; i++) {
      startOffset += this.columns.getIn([i, 'width']);
    }
    return resizePoints.reduce((points, offset) => {
      if (points.length === 0) {
        return [{
          start: startOffset,
          width: offset,
          offset
        }];
      }
      const lastPoint = points[points.length - 1];
      points.push({
        start: lastPoint.offset + startOffset,
        width: offset - lastPoint.offset,
        offset
      });
      return points;
    }, []);
  }
  startColumnResize(columnIndex, widths) {
    const resizingIndex = Math.max(columnIndex, 0);
    const resizePoints = this.getResizePoints(resizingIndex, widths);
    if (columnIndex >= 0 || columnIndex < this.props.section.get('columns').size) {
      this.setState({
        resizePoints,
        resizingIndex,
        columnsResizeWidth: widths
      });
    }
  }
  stopColumnResize(columnIndex, newWidth) {
    if (newWidth) {
      this.resizeColumns(columnIndex, newWidth, null, true);
    } else {
      const widths = this.getFinalColumnWidthsFromState(this.state.columnsResizeWidth);
      const oldWidths = this.props.section.get('columns').map(c => c.get('width'));
      if (JSON.stringify(widths) === JSON.stringify(oldWidths)) {
        this.setState({
          resizingIndex: -1
        });
      } else {
        logInteraction({
          action: 'Modify Layout Action',
          event: 'resize-columns',
          numColumns: this.props.section.get('columns').size
        });
        this.props.resizeColumns(this.props.index, widths);
      }
    }
  }
  resizeColumns(columnIndex, newWidth, newHardWidthChange, isFinalResize = false) {
    const {
      index,
      section
    } = this.props;
    const {
      columnsResizeWidth
    } = this.state;
    const savedColumnWidths = [section.getIn(['columns', columnIndex, 'width']), section.getIn(['columns', columnIndex + 1, 'width'])];
    const widthTogether = savedColumnWidths[0] + savedColumnWidths[1];
    const min = 3;
    const max = widthTogether - 3;
    columnsResizeWidth[0] = Math.min(Math.max(newWidth, min), max);
    columnsResizeWidth[1] = widthTogether - columnsResizeWidth[0];
    if (isFinalResize) {
      const widths = this.getFinalColumnWidthsFromState(columnsResizeWidth);
      const oldWidths = this.props.section.get('columns').map(c => c.get('width'));
      if (JSON.stringify(widths) === JSON.stringify(oldWidths)) {
        this.setState({
          resizingIndex: -1
        });
      } else {
        logInteraction({
          action: 'Modify Layout Action',
          event: 'resize-columns',
          numColumns: this.props.section.get('columns').size
        });
        this.props.resizeColumns(index, widths);
      }
    } else {
      this.setState({
        columnsResizeWidth
      });
    }
  }
  getFinalColumnWidthsFromState(columnWidthsChanged) {
    const {
      section
    } = this.props;
    const {
      resizingIndex
    } = this.state;
    return section.get('columns').map((c, i) => columnWidthsChanged[i - resizingIndex] || c.get('width'));
  }
  maybeRenderDragHandles() {
    const {
      canAddNewColumn,
      canResize,
      containerRef,
      index,
      isMobileBreakpointMode,
      isSelected,
      minColumnWidth,
      resizeGraduationSize,
      section,
      showResize
    } = this.props;
    if (!canResize || !showResize || !isSelected) {
      return null;
    }
    const numColumns = section.get('columns').size;
    const workingDragHandles = this.columns.map((c, i, cols) => {
      if (i === cols.size - 1) return null;
      const widths = [c.get('width'), cols.getIn([i + 1, 'width'])];
      const isDisabled = widths[0] + widths[1] <= minColumnWidth * 2;
      return /*#__PURE__*/_jsx(AsyncColumnResizeHandle, {
        columnIndex: i,
        resizeColumns: this.resizeColumns,
        currentWidth: c.get('width'),
        startColumnResize: this.startColumnResize,
        stopColumnResize: this.stopColumnResize,
        isResizing: this.state.resizingIndex > -1,
        widths: widths,
        isDisabled: isDisabled,
        offset: this.resizeHandleOffsets[i],
        resizeGraduationSize: resizeGraduationSize,
        isSelected: isSelected,
        containerRef: containerRef,
        numColumns: numColumns
      }, i);
    });
    if (isMobileBreakpointMode) {
      if (section.getIn(['style', 'stack']) === STACK.NONE) {
        return workingDragHandles;
      }
      return null;
    }
    const firstColumnWidth = this.columns.getIn([0, 'width']);
    const lastColumnWidth = this.columns.getIn([this.columns.size - 1, 'width']);
    return [/*#__PURE__*/_jsx(AsyncColumnResizeHandle, {
      columnIndex: -1,
      resizeColumns: this.resizeColumns,
      currentWidth: 0,
      addNewColumn: () => {
        logInteraction({
          action: 'Modify Layout Action',
          event: 'add-column',
          numColumns: section.get('columns').size,
          addedAt: 'start'
        });
        this.props.addNewColumn(this.props.index, 0);
      },
      removeColumn: () => {
        logInteraction({
          action: 'Modify Layout Action',
          event: 'remove-column',
          numColumns: this.props.section.get('columns').size,
          removedAt: 'start'
        });
        this.props.removeColumn(index, 0);
      },
      isResizing: this.state.resizingIndex > -1,
      widths: [minColumnWidth, firstColumnWidth - minColumnWidth],
      canAddNewColumn: canAddNewColumn,
      canRemoveColumn: this.columns.size > 1,
      offset: 0,
      isSelected: isSelected,
      resizeGraduationSize: resizeGraduationSize,
      updatesNumColumns: true,
      containerRef: containerRef
    }, -1), ...workingDragHandles, /*#__PURE__*/_jsx(AsyncColumnResizeHandle, {
      columnIndex: this.columns.size,
      resizeColumns: this.resizeColumns,
      currentWidth: this.columns.getIn([this.columns.size - 1, 'width']),
      isRightSide: true,
      addNewColumn: () => {
        logInteraction({
          action: 'Modify Layout Action',
          event: 'add-column',
          numColumns: this.props.section.get('columns').size,
          addedAt: 'end'
        });
        this.props.addNewColumn(index, this.columns.size);
      },
      removeColumn: () => {
        logInteraction({
          action: 'Modify Layout Action',
          event: 'remove-column',
          numColumns: this.props.section.get('columns').size,
          removedAt: 'end'
        });
        this.props.removeColumn(index, this.columns.size - 1);
      },
      isSelected: isSelected,
      isResizing: this.state.resizingIndex > -1,
      widths: [lastColumnWidth - minColumnWidth, minColumnWidth],
      canAddNewColumn: canAddNewColumn,
      canRemoveColumn: this.columns.size > 1,
      offset: resizeGraduationSize * 12,
      resizeGraduationSize: resizeGraduationSize,
      updatesNumColumns: true,
      containerRef: containerRef,
      numColumns: numColumns
    }, this.columns.size)];
  }
  maybeRenderResizeArea() {
    const {
      resizePoints,
      resizingIndex
    } = this.state;
    if (!resizePoints || !resizePoints.length || resizingIndex < 0) {
      return null;
    }
    const column = this.columns.get(resizingIndex);
    return resizePoints.map(point => /*#__PURE__*/_jsx(AsyncResizeArea, Object.assign({}, point, {
      columnWidth: column.get('width'),
      columnIndex: resizingIndex,
      sectionIndex: this.props.index,
      resizeColumns: this.resizeColumns,
      resizeGraduationSize: this.props.resizeGraduationSize
    }), point.offset));
  }
  render() {
    const {
      section,
      isDragging,
      selectable,
      index,
      numSections,
      isSingleColumnFullWidth,
      isSelected,
      canResize,
      resizeGraduationSize,
      canAddNewColumn,
      isOverChildElement,
      setIsHoveringSubSection
    } = this.props;
    const stack = section.getIn(['style', 'stack']);
    const verticalAlign = getDefaultStyle(section.get('style'), 'verticalAlign') || VERTICAL_ALIGN.TOP;
    const columnsInSourceOrder = stack === STACK.RIGHT_TO_LEFT ? this.columns.reverse() : this.columns;
    return /*#__PURE__*/_jsxs(RowDOM, {
      isDragging: isDragging,
      section: section,
      selectable: selectable,
      index: index,
      numSections: numSections,
      isSingleColumnFullWidth: isSingleColumnFullWidth,
      canResize: canResize,
      children: [!this.props.children ? columnsInSourceOrder.map((column, i, columns) => {
        const colId = column.get('id');
        return /*#__PURE__*/_jsx(Column, {
          column: column,
          columnWidthToRight: i < columns.size && columns.getIn([i + 1, 'width']),
          numColumns: columns.size,
          index: i,
          sectionIndex: index,
          sectionId: section.get('id'),
          selectable: selectable && !isSelected,
          isSingleColumnFullWidth: isSingleColumnFullWidth,
          resizeColumns: this.resizeColumns,
          startColumnResize: this.startColumnResize,
          stopColumnResize: this.stopColumnResize,
          canResize: canResize,
          resizePoints: this.state.resizingIndex === i ? this.state.resizePoints : [],
          isOverChildElement: isOverChildElement,
          isSectionSelected: isSelected,
          isResizing: this.state.resizingIndex > -1,
          resizeGraduationSize: resizeGraduationSize,
          canAddNewColumn: canAddNewColumn,
          setIsHoveringSubSection: setIsHoveringSubSection,
          stack: stack,
          verticalAlign: verticalAlign
        }, colId);
      }) : this.props.children, this.maybeRenderDragHandles(), this.maybeRenderResizeArea()]
    });
  }
}
RowDOMContainer.propTypes = {
  showResize: PropTypes.bool.isRequired,
  canResize: PropTypes.bool.isRequired,
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isSingleColumnFullWidth: PropTypes.bool.isRequired,
  numSections: PropTypes.number.isRequired,
  section: PropTypes.object.isRequired,
  selectable: PropTypes.bool.isRequired,
  isOverChildElement: PropTypes.bool,
  isMobileBreakpointMode: PropTypes.bool.isRequired,
  //resize props
  minColumnWidth: PropTypes.number,
  resizeColumns: PropTypes.func,
  resizeGraduationSize: PropTypes.number,
  addNewColumn: PropTypes.func,
  removeColumn: PropTypes.func,
  canAddNewColumn: PropTypes.bool,
  containerRef: PropTypes.object,
  //drag props
  isDragging: PropTypes.bool,
  setIsHoveringSubSection: PropTypes.func
};
RowDOMContainer.defaultProps = {
  minColumnWidth: 3,
  resizeGraduationSize: 50,
  isOverChildElement: false
};
export default connect(mapStateToProps, {
  resizeColumns,
  addNewColumn,
  removeColumn
})(RowDOMContainer);